// Helpers stateless pour les phases 3 et 4 de la chaine de readiness RMF. Partages entre :
//   - sessionStore (au cold start, apres /status=ready)
//   - worldSwitch (au switch-world, apres switching=false && current_world===target)
//
// Les deux flows ont besoin de la meme sequence : poll /health/full jusqu'a phase=fully_ready,
// puis poll /api/fleets >= expectedFleets ET /api/building_map=200. Factorise ici pour rester
// cohérent avec scripts/e2e-rmf-full.sh (T2 + T2.5) et scripts/test-matrix-dispatch.sh
// (switch_world_to phase 1 + phase 2).
//
// Le retry sur timeout phase 4 (bug task #36) reste cote appelant : seul le caller sait s'il
// veut tenter un POST /switch-world idempotent pour rebooter les fleet_adapter.

import { backendClient, type RmfBuildingMap } from './backend-client';
import { SessionFailedError, SessionNotReadyError } from './errors';

/** Timeout par phase. Aligne sur scripts/e2e-rmf-full.sh : 150s par phase, 5s d'intervalle.
 *  Cold start total = 300s en pire cas avant degraded. Logique deliberement simple :
 *  while (elapsed < timeout) { check ; sleep } puis verdict, comme le script bash backend. */
const FULLY_READY_TIMEOUT_MS = 150_000;
const RMF_READY_TIMEOUT_MS = 150_000;
const POLL_INTERVAL_MS = 5_000;

/** Bail-out rapide : une fois que building_map=200 est arrive (= rmf-web a charge le world,
 *  graphes nav prets), si fleets < expected n'a pas evolue pendant ce delai, on sort en
 *  degraded sans attendre le RMF_READY_TIMEOUT_MS complet. Hypothese : si rmf-web sert deja
 *  le building_map, les fleet_adapters manquants sont probablement crashes/bloques cote
 *  backend, continuer a poller ne les ressuscitera pas. Le caller peut alors decider
 *  immediatement entre retry (POST /switch-world idempotent) et acceptation degradee.
 *
 *  Different de la "stagnation detection" Phase 8.5+ (retiree) : la c'etait une heuristique
 *  generique sur fleets-ne-bouge-pas, qui masquait des bugs backend reels. Ici on a un signal
 *  precis (building_map ready) qui borne notre patience. */
const FAST_DEGRADED_AFTER_BUILDING_MAP_MS = 15_000;

/** Phases observables par le caller pendant la chaine. Mappent directement sur BackendPhase. */
export type ReadinessPhase = 'rmf_initializing' | 'rmf_loading' | 'ready';

/** Snapshot intermediaire envoye au caller via onPhase. fleetsCount est present uniquement
 *  pendant 'rmf_loading' et 'ready' (pas avant qu'on commence a poll /api/fleets). */
export type ReadinessProgress = {
  phase: ReadinessPhase;
  fleetsCount?: number;
};

/** Resultat final de la chaine. `degraded=true` signale que phase 4 a timeout sans atteindre
 *  expectedFleets : le caller peut soit retry (POST /switch-world idempotent), soit accepter
 *  l'etat degrade et alerter le user. `buildingMap` est le payload fetched pendant phase 4 :
 *  on l'expose pour qu'un caller (sessionStore) puisse le pousser dans tasksStore et eviter un
 *  refetch redondant qui retomberait sur un 404 si rmf-web hiccup juste apres. null si phase 4
 *  est sortie en degraded sans avoir reussi a chopper le building_map. */
export type ReadinessResult = {
  degraded: boolean;
  fleetsCount: number;
  buildingMap: RmfBuildingMap | null;
};

/**
 * Attend que la chaine RMF complete soit servable.
 *
 * Phase 3 (rmf_initializing) : poll GET /health/full jusqu'a phase=fully_ready. Throw
 * SessionNotReadyError sur timeout, SessionFailedError sur phase=failed.
 *
 * Phase 4 (rmf_loading) : poll GET /api/fleets jusqu'a count >= expectedFleets ET
 * GET /api/building_map jusqu'a 200. Sur timeout, NE throw PAS : retourne degraded=true
 * pour que le caller decide entre retry et acceptation degradee.
 *
 * Le predicat cancelled() est lu entre chaque poll pour permettre un cleanup propre quand
 * l'utilisateur navigue ailleurs ou declenche un /stop pendant la chaine.
 */
export async function waitUntilRmfReady(opts: {
  expectedFleets: number;
  cancelled: () => boolean;
  onPhase?: (progress: ReadinessProgress) => void;
}): Promise<ReadinessResult> {
  // Phase 3 : /health/full -> fully_ready.
  opts.onPhase?.({ phase: 'rmf_initializing' });
  const phase3Deadline = Date.now() + FULLY_READY_TIMEOUT_MS;
  while (!opts.cancelled()) {
    if (Date.now() >= phase3Deadline) {
      throw new SessionNotReadyError('Timeout en attente de phase=fully_ready (health/full)');
    }
    try {
      const health = await backendClient.getHealthFull();
      if (health.phase === 'fully_ready') break;
      if (health.phase === 'failed') {
        throw new SessionFailedError('La chaine RMF a echoue (health.phase=failed)');
      }
    } catch (err) {
      // Network/timeout transient (rmf-web pas encore bound, AbortSignal timeout 3s dans le
      // client real) : on retry court sans casser le poll. Le deadline finira par throw si ca
      // persiste plus de 150 s (FULLY_READY_TIMEOUT_MS), y compris un echec TLS navigateur
      // (cf ADR 035).
      if (err instanceof SessionFailedError) throw err;
      console.warn('health/full poll failed (retry):', err);
    }
    await sleep(POLL_INTERVAL_MS);
  }
  if (opts.cancelled()) return { degraded: false, fleetsCount: 0, buildingMap: null };

  // Phase 4 : fleets + building_map. Boucle simple alignee sur scripts/e2e-rmf-full.sh.
  // Deux exits :
  //   1. Succes : fleets >= expected ET building_map=200 -> ready
  //   2. Hard timeout 150s : caller decide (degraded ou retry switch-world)
  // Pas de stagnation detection (retiree Phase 8.5+ : trop d'edge cases et masquait des bugs
  // backend reels). On loope jusqu'au timeout, comme le script bash backend.
  opts.onPhase?.({ phase: 'rmf_loading', fleetsCount: 0 });
  const phase4Deadline = Date.now() + RMF_READY_TIMEOUT_MS;
  let lastFleetsCount = 0;
  let lastBuildingMap: RmfBuildingMap | null = null;
  // Timestamp du moment ou building_map devient disponible pour la 1ere fois. Sert au
  // bail-out rapide ci-dessous : si fleets < expected n'evolue pas pendant
  // FAST_DEGRADED_AFTER_BUILDING_MAP_MS apres ce timestamp, on sort degraded.
  let buildingMapReadyAt: number | null = null;
  // Pour le log de status periodique : on log au 1er fail + tous les ~20s ensuite plutot que
  // a chaque iteration. Reduit le bruit console (vs 4-5 logs/s pendant 150s).
  let lastStatusLogAt = 0;
  const STATUS_LOG_INTERVAL_MS = 20_000;
  while (!opts.cancelled()) {
    if (Date.now() >= phase4Deadline) {
      return { degraded: true, fleetsCount: lastFleetsCount, buildingMap: lastBuildingMap };
    }
    let fleetsOk = false;
    try {
      lastFleetsCount = await backendClient.getFleetsCount();
      opts.onPhase?.({ phase: 'rmf_loading', fleetsCount: lastFleetsCount });
      fleetsOk = lastFleetsCount >= opts.expectedFleets;
    } catch {
      // Network error : on log via le throttled status log ci-dessous. Pas chaque error.
    }
    // Building_map fetch gate par fleetsCount > 0 : observation 2026-05-23 montre que
    // /api/building_map et /api/fleets dependent de la meme chaine RMF, fleets registers en
    // premier puis building_map suit. Tenter building_map alors qu'aucune fleet n'est registree
    // donne systematiquement 404 (rmf_core pas pret) -> appels inutiles + noise console. On
    // attend qu'au moins une fleet soit la avant d'essayer. En degraded (fleets jamais reach
    // expected mais > 0), on continue de tenter building_map a chaque iter.
    if (lastFleetsCount > 0 && !lastBuildingMap) {
      try {
        lastBuildingMap = await backendClient.getBuildingMap();
        if (lastBuildingMap) buildingMapReadyAt = Date.now();
      } catch {
        // Throttle dans le status log periodique ci-dessous.
      }
    }
    const buildingOk = lastBuildingMap !== null;
    if (fleetsOk && buildingOk) {
      opts.onPhase?.({ phase: 'ready', fleetsCount: lastFleetsCount });
      return { degraded: false, fleetsCount: lastFleetsCount, buildingMap: lastBuildingMap };
    }
    // Bail-out rapide : building_map=200 sert de borne de patience pour les fleets manquantes.
    // Si on est dans la fenetre [buildingMapReadyAt, +FAST_DEGRADED_AFTER_BUILDING_MAP_MS] et
    // que fleets n'a toujours pas reach expected, on sort degraded sans attendre le timeout
    // complet 150s. Le caller (sessionStore/worldSwitch) gere ensuite l'UI degraded + retry.
    if (
      buildingMapReadyAt !== null &&
      !fleetsOk &&
      Date.now() - buildingMapReadyAt >= FAST_DEGRADED_AFTER_BUILDING_MAP_MS
    ) {
      console.warn(
        `[readiness] fast-degraded exit: building_map=200 since ${Math.round((Date.now() - buildingMapReadyAt) / 1000)}s but fleets=${lastFleetsCount}/${opts.expectedFleets}`
      );
      return { degraded: true, fleetsCount: lastFleetsCount, buildingMap: lastBuildingMap };
    }
    // Status log throttled, DEV uniquement : utile pour suivre l'avancement en dev sans saturer
    // la console, et sans laisser de bruit periodique en prod (gate sur import.meta.env.DEV).
    // Format aligne sur scripts/e2e-rmf-full.sh : `[elapsed_s] fleets=N/M building_map=200|404`.
    const elapsedSec = Math.round((Date.now() - (phase4Deadline - RMF_READY_TIMEOUT_MS)) / 1000);
    if (import.meta.env.DEV && Date.now() - lastStatusLogAt >= STATUS_LOG_INTERVAL_MS) {
      console.log(
        `[readiness ${elapsedSec}s] fleets=${lastFleetsCount}/${opts.expectedFleets} building_map=${buildingOk ? '200' : '404'}`
      );
      lastStatusLogAt = Date.now();
    }
    await sleep(POLL_INTERVAL_MS);
  }
  return { degraded: false, fleetsCount: lastFleetsCount, buildingMap: lastBuildingMap };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
